import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api';
import type { BeaconMismatch, OnboardingStatus } from '../../../types';

const SNIPPET_TEMPLATE = (domainId: string) =>
  `<script src="https://api.fixmyseo.io/cdn/tag/${domainId}.js" async></script>`;

const FAST_INTERVAL_MS = 10_000;   // first 2 minutes
const SLOW_INTERVAL_MS = 30_000;   // after 2 minutes
const FAST_PHASE_MS    = 2 * 60_000;

interface Props {
  domainId: string;
  domain: string;
  status: OnboardingStatus;
  onStatusUpdate: (next: OnboardingStatus) => void;
  onContinue: () => void;
}

export function VerificationStep({ domainId, domain, status, onStatusUpdate, onContinue }: Props) {
  const snippet = SNIPPET_TEMPLATE(domainId);
  const [copied, setCopied] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mismatches, setMismatches] = useState<BeaconMismatch[]>([]);
  const [misLoading, setMisLoading] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);

  const startTimeRef  = useRef(Date.now());
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Elapsed counter ───────────────────────────────────────────────────────

  useEffect(() => {
    if (status.verified) return;
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, [status.verified]);

  // ── Polling ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (status.verified) return;

    const poll = async () => {
      try {
        const next = await api.onboarding.getStatus(domainId);
        if (next.verified) {
          onStatusUpdate(next);
          return; // stop polling
        }
      } catch {
        // ignore transient poll errors
      }
      const elapsed = Date.now() - startTimeRef.current;
      const interval = elapsed < FAST_PHASE_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
      pollTimerRef.current = setTimeout(poll, interval);
    };

    pollTimerRef.current = setTimeout(poll, FAST_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [domainId, status.verified, onStatusUpdate]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  function copySnippet() {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function openHelp() {
    setHelpOpen(true);
    setMisLoading(true);
    try {
      const rows = await api.onboarding.getBeaconMismatches(domainId, 5);
      setMismatches(rows);
    } catch {
      setMismatches([]);
    } finally {
      setMisLoading(false);
    }
  }

  function formatElapsed(sec: number): string {
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s}s`;
  }

  // ── Verified state ────────────────────────────────────────────────────────

  if (status.verified) {
    return (
      <div data-testid="verification-verified" className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6 flex items-start gap-4">
          <span className="text-2xl">✓</span>
          <div>
            <h2 className="text-lg font-semibold text-brand-green">Domain verified</h2>
            <p className="text-sm text-green-700 mt-1">
              Your snippet is installed and sending signals from <strong>{domain}</strong>.
            </p>
          </div>
        </div>

        <button
          onClick={onContinue}
          className="w-full bg-brand-green text-white font-medium rounded-xl py-3 text-sm hover:bg-brand-green-dark transition-colors"
        >
          Continue to step 2 →
        </button>
      </div>
    );
  }

  // ── Unverified state ──────────────────────────────────────────────────────

  return (
    <div data-testid="verification-pending" className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-brand-ink mb-1">Install your snippet</h2>
        <p className="text-sm text-brand-muted">
          Paste the script tag below into the <code className="bg-gray-100 px-1 rounded">&lt;head&gt;</code> of every
          page on <strong>{domain}</strong>. We'll detect it automatically.
        </p>
      </div>

      {/* Snippet code block */}
      <div className="relative">
        <pre
          data-testid="snippet-code"
          className="bg-brand-ink text-brand-paper text-xs rounded-xl p-4 overflow-x-auto font-mono whitespace-pre-wrap break-all"
        >
          {snippet}
        </pre>
        <button
          onClick={copySnippet}
          data-testid="copy-snippet"
          className="absolute top-2 right-2 text-xs bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded transition-colors"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Waiting indicator */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-sm text-brand-ink">Waiting for first signal…</span>
        </div>
        <span className="text-xs text-brand-muted tabular-nums">{formatElapsed(elapsedSec)}</span>
      </div>

      {/* How verification triggers */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-sm text-blue-800 space-y-1">
        <p className="font-medium">How to trigger verification</p>
        <p>
          Once the snippet is on your site, simply{' '}
          <strong>open your website in any browser tab</strong>. The tag fires automatically
          when the page loads — this page will update within seconds.
        </p>
        <p className="text-blue-600 text-xs">
          Not ready to deploy yet? You can close this page and come back later — your progress
          is saved and we'll pick up right here when you return.
        </p>
      </div>

      {/* Platform guides */}
      <div className="text-sm text-gray-500 space-y-1">
        <p className="font-medium text-brand-ink text-sm">Platform guides:</p>
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>
            <a
              href="https://support.google.com/sites/answer/2722257"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green hover:underline"
            >
              Google Sites
            </a>
          </li>
          <li>
            <a
              href="https://support.squarespace.com/hc/en-us/articles/205815908"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green hover:underline"
            >
              Squarespace
            </a>
          </li>
          <li>
            <a
              href="https://help.wix.com/en/article/embedding-custom-code-to-your-site"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green hover:underline"
            >
              Wix
            </a>
          </li>
          <li>
            WordPress: add to your theme's{' '}
            <code className="bg-gray-100 px-1 rounded">functions.php</code> via{' '}
            <code className="bg-gray-100 px-1 rounded">wp_head</code> hook, or use a
            header/footer plugin.
          </li>
        </ul>
      </div>

      <button
        onClick={openHelp}
        className="text-sm text-brand-muted hover:text-brand-ink underline"
      >
        Still waiting? Get help →
      </button>

      {/* ── Help modal ── */}
      {helpOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Verification help"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setHelpOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4 overflow-y-auto max-h-[80vh]">
            <div className="flex justify-between items-start">
              <h3 className="text-lg font-semibold text-brand-ink">Verification troubleshooting</h3>
              <button
                onClick={() => setHelpOpen(false)}
                className="text-brand-muted hover:text-brand-ink text-sm"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-brand-ink">Your snippet</p>
              <pre className="bg-brand-ink text-brand-paper text-xs rounded-lg p-3 overflow-x-auto font-mono whitespace-pre-wrap break-all">
                {snippet}
              </pre>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-brand-ink">Common issues</p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>Snippet added to only one page — it must be on every page</li>
                <li>Cache plugin serving an old version of your HTML</li>
                <li>Content Security Policy blocking external scripts</li>
                <li>Snippet placed inside a conditional block that doesn't always render</li>
                <li>Ad blocker or browser extension blocking the request</li>
              </ul>
            </div>

            {/* Beacon mismatches */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-brand-ink">
                Recent host mismatches
                <span className="text-xs text-brand-muted font-normal ml-2">
                  (signals received from unexpected domains)
                </span>
              </p>
              {misLoading && (
                <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
              )}
              {!misLoading && mismatches.length === 0 && (
                <p className="text-xs text-brand-muted">No mismatches recorded.</p>
              )}
              {!misLoading && mismatches.length > 0 && (
                <ul className="space-y-1">
                  {mismatches.map((m) => (
                    <li key={m.id} className="text-xs text-gray-600 font-mono bg-gray-50 rounded px-2 py-1">
                      {m.hostReceived}
                      <span className="text-gray-400 ml-2">
                        {new Date(m.ts).toLocaleTimeString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              onClick={() => setHelpOpen(false)}
              className="w-full bg-brand-green text-white font-medium rounded-xl py-2.5 text-sm hover:bg-brand-green-dark transition-colors"
            >
              Got it — keep waiting
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
